import { supabase } from "../supabaseClient";

// CP payment entries can only be torn down from these Status values.
const DELETABLE_CP_PAYMENT_STATUSES = ["pending for invoice", "rejected"];

// Lead Source Change payment gate — CpPayments.Status buckets (case-insensitive).
const GATE_AUTO_DELETE_STATUSES = ["pending for invoice"];
const GATE_BLOCKED_STATUSES = ["inreview", "approved", "payment in process", "rejected"];
const GATE_PERMANENT_BLOCK_STATUSES = ["partial payment done", "full payment done"];

/**
 * CP-payment-status gate for Lead Source Change (spec §2 step 2).
 * Reads the current, non-deleted "Basic" CpPayments row for this allotment
 * (same lookup key as deleteCpPaymentEntry — allotmentPaymentId, not CpId)
 * and classifies it into the 3-way branch the flow needs to act on.
 *
 * Returns one of:
 *   { action: "none" }                          — no Basic CP payment row exists yet
 *   { action: "auto_delete", cpPaymentId }       — Pending For Invoice
 *   { action: "block", message }                 — InReview / Approved / Payment In Process / Rejected
 *   { action: "block_permanent", message }       — Partial/Full Payment Done
 *   { action: "block", message }                 — any other/unrecognized status (open set —
 *                                                    fail safe rather than silently pass)
 */
export const getCpPaymentGateStatus = async (allotmentPaymentId) => {
  try {
    if (!allotmentPaymentId) return { action: "none" };

    const { data: cpPayments, error } = await supabase
      .from("CpPayments")
      .select("id, Status")
      .eq("allotmentPaymentId", allotmentPaymentId)
      .eq("BrokerageType", "Basic")
      .or("isDeleted.is.null,isDeleted.eq.false")
      .limit(1);

    if (error) throw error;
    const cpPayment = cpPayments?.[0] || null;
    if (!cpPayment) return { action: "none" };

    const status = String(cpPayment.Status || "").toLowerCase().trim();

    if (GATE_AUTO_DELETE_STATUSES.includes(status)) {
      return { action: "auto_delete", cpPaymentId: cpPayment.id };
    }

    if (GATE_BLOCKED_STATUSES.includes(status)) {
      return {
        action: "block",
        message: `This lead has an existing CP payment entry with status "${cpPayment.Status}" — please delete that CP payment entry first (from CP Payout) before changing the lead source.`,
      };
    }

    if (GATE_PERMANENT_BLOCK_STATUSES.includes(status)) {
      return {
        action: "block_permanent",
        message: "Cannot change — payment already transferred to the existing CP.",
      };
    }

    return {
      action: "block",
      message: `Cannot change — unrecognized CP payment status "${cpPayment.Status}". Contact support before proceeding.`,
    };
  } catch (error) {
    console.error("❌ getCpPaymentGateStatus failed:", error);
    return {
      action: "block",
      message: "Failed to check CP payment status. Please try again.",
    };
  }
};

/**
 * Soft-deletes a CP payment entry for a Basic-brokerage booking and reopens
 * it for a fresh CP payment entry — used by the CP Payout "Basic Brokerage"
 * card delete action.
 *
 * Flow: AllotmentPayment -> CpPayments (BrokerageType = "Basic", all linked
 * CpInvoices must be REJECTED) -> flag CpPayments deleted -> insert one
 * CpPaymentLogs audit row (Type = "Deleted") -> cascade-flag linked CpInvoices
 * deleted -> AllotmentPayment.IsCpPaymentEntryDone = false so the cron
 * regenerates a fresh entry next cycle.
 *
 * Nothing is ever hard-deleted — CpPayments/CpInvoices rows are flagged
 * (isDeleted = true, Status = "Deleted") and kept as history, and prior
 * CpPaymentLogs rows are never removed.
 */
export const deleteCpPaymentEntry = async (allotmentPaymentId, note) => {
  try {
    if (!allotmentPaymentId) throw new Error("Missing allotmentPaymentId");

    const trimmedNote = String(note || "").trim();
    if (!trimmedNote) throw new Error("A note is required to delete a CP payment entry.");

    const { data: cpPayments, error: cpPaymentError } = await supabase
      .from("CpPayments")
      .select("id, Status")
      .eq("allotmentPaymentId", allotmentPaymentId)
      .eq("BrokerageType", "Basic")
      .or("isDeleted.is.null,isDeleted.eq.false")
      .limit(1);

    if (cpPaymentError) throw cpPaymentError;

    const cpPayment = cpPayments?.[0] || null;

    if (!cpPayment) {
      return {
        success: false,
        blocked: true,
        message: "No CP payment entry found for this booking.",
      };
    }

    const status = String(cpPayment.Status || "").toLowerCase().trim();
    if (!DELETABLE_CP_PAYMENT_STATUSES.includes(status)) {
      return {
        success: false,
        blocked: true,
        message: `Cannot delete — CP payment status is "${cpPayment.Status}". Only "Pending For Invoice" or "REJECTED" entries can be deleted.`,
      };
    }

    const cpPaymentId = cpPayment.id;

    // Descending order so, if more than one invoice is linked (e.g. rejected +
    // resubmitted), the most recently created one is the one logged below.
    const { data: invoices, error: invoicesError } = await supabase
      .from("CpInvoices")
      .select("invoicesId, FV_Inword_Status")
      .eq("CPpaymentId", cpPaymentId)
      .order("invoicesId", { ascending: false });

    if (invoicesError) throw invoicesError;

    const hasNonRejectedInvoice = (invoices || []).some(
      (inv) => String(inv.FV_Inword_Status || "").toLowerCase().trim() !== "rejected"
    );

    if (hasNonRejectedInvoice) {
      return {
        success: false,
        blocked: true,
        message: "Cannot delete — one or more invoices raised against this CP payment are not rejected.",
      };
    }

    const mostRecentRejectedInvoiceId = invoices?.[0]?.invoicesId ?? null;

    const { error: paymentFlagError } = await supabase
      .from("CpPayments")
      .update({ isDeleted: true, Status: "Deleted" })
      .eq("id", cpPaymentId);

    if (paymentFlagError) throw paymentFlagError;

    const { error: logsError } = await supabase
      .from("CpPaymentLogs")
      .insert([
        {
          CPpaymentId: cpPaymentId,
          Type: "Deleted",
          Message: trimmedNote,
          CpInvoiceId: mostRecentRejectedInvoiceId,
        },
      ]);

    if (logsError) throw logsError;

    if ((invoices || []).length > 0) {
      const { error: invoicesFlagError } = await supabase
        .from("CpInvoices")
        .update({ isDeleted: true, Status: "Deleted" })
        .eq("CPpaymentId", cpPaymentId);

      if (invoicesFlagError) throw invoicesFlagError;
    }

    const { error: flagError } = await supabase
      .from("AllotmentPayment")
      .update({ IsCpPaymentEntryDone: false })
      .eq("allotmentPaymentId", allotmentPaymentId);

    if (flagError) throw flagError;

    return { success: true };
  } catch (error) {
    console.error("❌ deleteCpPaymentEntry failed:", error);
    return {
      success: false,
      blocked: false,
      message: error.message || "Failed to delete CP payment entry.",
    };
  }
};
